import { SalesRepConfig } from '@domain/configuration/types';
import { useAuthenticatedUserUUID } from '@/contexts/auth-context';
import { useChatSalesPortalDynamicConfigValue } from '@/experiences/sales-portal/blocs/hooks/use-chat-dynamic-config';
import { useEmployeeConfigLoader } from '@/domain/configuration/blocs/loaders/use-employee-config-loader';
import { useMemo } from 'react';

/**
 * Hook to fetch the sales rep config for a supplier.
 *
 * Reads `sales_rep_config` from dynamic config, falling back through:
 * employee config → chat config → supplier config → null.
 *
 * @param supplierUUID - The supplier identifier
 * @param chatUUID - Optional chat identifier for chat-specific config
 * @returns The sales rep config
 *
 * @example
 * ```typescript
 * const { loading, salesRepConfig } = useSalesRepConfig(supplierUUID, chatUUID);
 *
 * const isDisabled = salesRepConfig?.override_mode === SalesRepOverrideMode.DISABLED;
 * ```
 */
export const useSalesRepConfig = (
  supplierUUID: string,
  chatUUID?: string | null,
): { loading: boolean; salesRepConfig: SalesRepConfig | null } => {
  const { value: rawSalesRepConfig, loading: dynamicConfigLoading } =
    useChatSalesPortalDynamicConfigValue(
      supplierUUID,
      'sales_rep_config',
      chatUUID,
    );
  const userUUID = useAuthenticatedUserUUID();
  const { employeeConfig, loading: employeeConfigLoading } =
    useEmployeeConfigLoader(supplierUUID, userUUID);
  const employeePriceFloorMode = employeeConfig.priceFloorMode;

  const salesRepConfig = useMemo<SalesRepConfig | null>(() => {
    if (!rawSalesRepConfig) {
      return null;
    }
    return {
      ...rawSalesRepConfig,
      price_floor_mode:
        employeePriceFloorMode ?? rawSalesRepConfig.price_floor_mode ?? null,
    };
  }, [rawSalesRepConfig, employeePriceFloorMode]);

  return {
    loading: dynamicConfigLoading || employeeConfigLoading,
    salesRepConfig,
  };
};
